import {
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  CircleCheck,
  Clock,
  Hourglass,
  MessageSquare,
  Minus,
  RotateCcw,
  Ticket as TicketIcon,
  TriangleAlert,
  UserCheck,
} from 'lucide-react'
import { type ReactNode } from 'react'

import type { TicketPriority, TicketStatus } from '@aeris/communication'
import {
  TICKET_EVENT_LABELS,
  TICKET_PRIORITY_TEXT,
  TICKET_STATUS_TEXT,
} from '@aeris/status-meta'
import { type StatusTone } from '@aeris/ui'

/**
 * Os rótulos e as cores de um chamado (F051), compartilhados entre a LISTA
 * (`chamados.tsx`) e o DETALHE (`chamados-detalhe.tsx`, F061).
 *
 * Vive aqui desde que o detalhe virou rota própria: duas cópias de "urgente é
 * vermelho" divergem na primeira vez que alguém mexe numa só, e a tela que
 * "não foi tocada" fica com a versão velha sem ninguém perceber.
 *
 * **Só constantes, nenhum componente** — um módulo que exporta as duas coisas
 * deixa de ser fronteira de Fast Refresh (`react-refresh/
 * only-export-components`). Os dois `Badge` que consomem isto ficam em
 * `components/ticket-badges.tsx`. Os textos moram em `@aeris/status-meta`
 * desde a F308: o celular fala igual.
 */

export const STATUS_META: Record<
  TicketStatus,
  { label: string; tone: StatusTone; icon: ReactNode }
> = {
  open: {
    ...TICKET_STATUS_TEXT.open,
    icon: <TicketIcon aria-hidden="true" className="h-3 w-3" />,
  },
  in_progress: {
    ...TICKET_STATUS_TEXT.in_progress,
    icon: <Clock aria-hidden="true" className="h-3 w-3" />,
  },
  waiting: {
    ...TICKET_STATUS_TEXT.waiting,
    icon: <Hourglass aria-hidden="true" className="h-3 w-3" />,
  },
  closed: {
    ...TICKET_STATUS_TEXT.closed,
    icon: <CircleCheck aria-hidden="true" className="h-3 w-3" />,
  },
}

export const PRIORITY_META: Record<
  TicketPriority,
  { label: string; tone: StatusTone; icon: ReactNode; hint: string }
> = {
  urgent: {
    ...TICKET_PRIORITY_TEXT.urgent,
    icon: <TriangleAlert aria-hidden="true" className="h-3 w-3" />,
  },
  high: {
    ...TICKET_PRIORITY_TEXT.high,
    icon: <ArrowUp aria-hidden="true" className="h-3 w-3" />,
  },
  normal: {
    ...TICKET_PRIORITY_TEXT.normal,
    icon: <Minus aria-hidden="true" className="h-3 w-3" />,
  },
  low: {
    ...TICKET_PRIORITY_TEXT.low,
    icon: <ArrowDown aria-hidden="true" className="h-3 w-3" />,
  },
}

export const EVENT_META: Record<string, { label: string; icon: ReactNode }> = {
  opened: {
    label: TICKET_EVENT_LABELS.opened ?? '',
    icon: <TicketIcon aria-hidden="true" className="h-3.5 w-3.5" />,
  },
  note: {
    label: TICKET_EVENT_LABELS.note ?? '',
    icon: <MessageSquare aria-hidden="true" className="h-3.5 w-3.5" />,
  },
  status_changed: {
    label: TICKET_EVENT_LABELS.status_changed ?? '',
    icon: <ArrowRightLeft aria-hidden="true" className="h-3.5 w-3.5" />,
  },
  assigned: {
    label: TICKET_EVENT_LABELS.assigned ?? '',
    icon: <UserCheck aria-hidden="true" className="h-3.5 w-3.5" />,
  },
  closed: {
    label: TICKET_EVENT_LABELS.closed ?? '',
    icon: <CircleCheck aria-hidden="true" className="h-3.5 w-3.5" />,
  },
  reopened: {
    label: TICKET_EVENT_LABELS.reopened ?? '',
    icon: <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />,
  },
}

export const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString('pt-BR')
